import { Component } from 'react'
import { toast } from 'react-toastify'
import { appRef } from '../../app'
import { getLocale, getString } from '../../locale'
import { Files, MyFile } from '../../components/files'
import { getBase64 } from '../../utils/base64'
import { HOST_URL } from '../../config'
import {
    Ltsp101CatalogSyrup,
    Ltsp101CatalogDrink,
    Ltsp101CatalogImage,
    listLtsp101CatalogSyrups,
    listLtsp101CatalogDrinks,
    listLtsp101CatalogImages,
    createLtsp101CatalogSyrup,
    updateLtsp101CatalogSyrup,
    deleteLtsp101CatalogSyrup,
    createLtsp101CatalogDrink,
    updateLtsp101CatalogDrink,
    deleteLtsp101CatalogDrink,
    createLtsp101CatalogImage,
    updateLtsp101CatalogImage,
    deleteLtsp101CatalogImage,
} from '../../api/ltsp101-catalog'
import { HiCollection } from 'react-icons/hi'

const LTSP101_NO_FLAVOUR_CHANNEL = 255
const LTSP101_BASE_BRIX_RATIO_A = 1
const LTSP101_BASE_BRIX_RATIO_B = 6

type TabKey = 'syrups' | 'drinks' | 'images'

type SyrupForm = {
    id?: number
    name: string
    description: string
    nameHe: string
    descriptionHe: string
    packageSize: number
    imageId?: number | null
}

type DrinkForm = {
    id?: number
    title: string
    description: string
    titleHe: string
    descriptionHe: string
    drinkType: 'juice' | 'soda' | 'base'
    baseChannel: number
    flavourChannel: number
    brixRatioA: number
    brixRatioB: number
}

type ImageForm = {
    id?: number
    title: string
    titleHe: string
    notes: string
    notesHe: string
    imageBase64: string
    imageMime: string
    imageName: string
}

type ImageUploadItem = {
    file: MyFile
    title: string
    titleHe: string
    notes: string
    notesHe: string
    imageBase64: string
    imageMime: string
    imageName: string
}

type State = {
    tab: TabKey
    search: string
    loading: boolean
    showDocs: boolean
    syrups: Ltsp101CatalogSyrup[]
    drinks: Ltsp101CatalogDrink[]
    images: Ltsp101CatalogImage[]
    showSyrupModal: boolean
    showDrinkModal: boolean
    showImageModal: boolean
    syrupMode: 'create' | 'edit'
    drinkMode: 'create' | 'edit'
    imageMode: 'create' | 'edit'
    syrupForm: SyrupForm
    drinkForm: DrinkForm
    imageForm: ImageForm
    imageFile?: MyFile
    imageUploads: ImageUploadItem[]
}

export class Ltsp101Catalog extends Component<{}, State> {
    state: State = {
        tab: 'syrups',
        search: '',
        loading: true,
        showDocs: false,
        syrups: [],
        drinks: [],
        images: [],
        showSyrupModal: false,
        showDrinkModal: false,
        showImageModal: false,
        syrupMode: 'create',
        drinkMode: 'create',
        imageMode: 'create',
        syrupForm: {
            name: '',
            description: '',
            nameHe: '',
            descriptionHe: '',
            packageSize: 0,
            imageId: null,
        },
        drinkForm: {
            title: '',
            description: '',
            titleHe: '',
            descriptionHe: '',
            drinkType: 'juice',
            baseChannel: 0,
            flavourChannel: LTSP101_NO_FLAVOUR_CHANNEL,
            brixRatioA: LTSP101_BASE_BRIX_RATIO_A,
            brixRatioB: LTSP101_BASE_BRIX_RATIO_B,
        },
        imageForm: {
            title: '',
            titleHe: '',
            notes: '',
            notesHe: '',
            imageBase64: '',
            imageMime: '',
            imageName: '',
        },
        imageFile: undefined,
        imageUploads: [],
    }

    componentDidMount = async () => {
        appRef.setHeader(<HiCollection className="me-2" size={28} />, () => getString('ltsp101_catalog.title'))
        await this.loadAll()
    }

    loadAll = async () => {
        try {
            this.setState({ loading: true })
            const [syrupsResult, drinksResult, imagesResult] = await Promise.all([
                listLtsp101CatalogSyrups(),
                listLtsp101CatalogDrinks(),
                listLtsp101CatalogImages(),
            ])
            this.setState({ syrups: syrupsResult.data, drinks: drinksResult.data, images: imagesResult.data })
        } catch (error: any) {
            toast.error(error?.message || getString('ltsp101_catalog.load_error'))
        } finally {
            this.setState({ loading: false })
        }
    }

    handleSearchChange = (value: string) => {
        this.setState({ search: value })
    }

    openCreateSyrup = () => {
        this.setState({
            showSyrupModal: true,
            syrupMode: 'create',
            imageFile: undefined,
            syrupForm: {
                name: '',
                description: '',
                nameHe: '',
                descriptionHe: '',
                packageSize: 0,
                imageId: null,
            },
        })
    }

    openEditSyrup = (syrup: Ltsp101CatalogSyrup) => {
        this.setState({
            showSyrupModal: true,
            syrupMode: 'edit',
            imageFile: undefined,
            syrupForm: {
                id: syrup.id,
                name: syrup.name,
                description: syrup.description,
                nameHe: syrup.nameHe,
                descriptionHe: syrup.descriptionHe,
                packageSize: syrup.packageSize,
                imageId: syrup.imageId ?? null,
            },
        })
    }

    closeSyrupModal = () => {
        this.setState({ showSyrupModal: false })
    }

    openCreateDrink = () => {
        this.setState({
            showDrinkModal: true,
            drinkMode: 'create',
            drinkForm: {
                title: '',
                description: '',
                titleHe: '',
                descriptionHe: '',
                drinkType: 'juice',
                baseChannel: 0,
                flavourChannel: LTSP101_NO_FLAVOUR_CHANNEL,
                brixRatioA: LTSP101_BASE_BRIX_RATIO_A,
                brixRatioB: LTSP101_BASE_BRIX_RATIO_B,
            },
        })
    }

    openCreateImage = () => {
        this.setState({
            showImageModal: true,
            imageMode: 'create',
            imageFile: undefined,
            imageUploads: [],
            imageForm: {
                title: '',
                titleHe: '',
                notes: '',
                notesHe: '',
                imageBase64: '',
                imageMime: '',
                imageName: '',
            },
        })
    }

    openEditDrink = (drink: Ltsp101CatalogDrink) => {
        const isBaseDrink = drink.flavourChannel === LTSP101_NO_FLAVOUR_CHANNEL
        this.setState({
            showDrinkModal: true,
            drinkMode: 'edit',
            drinkForm: {
                id: drink.id,
                title: drink.title,
                description: drink.description,
                titleHe: drink.titleHe,
                descriptionHe: drink.descriptionHe,
                drinkType: isBaseDrink ? 'base' : 'juice',
                baseChannel: drink.baseChannel,
                flavourChannel: drink.flavourChannel,
                brixRatioA: drink.brixRatioA,
                brixRatioB: drink.brixRatioB,
            },
        })
    }

    openEditImage = (image: Ltsp101CatalogImage) => {
        this.setState({
            showImageModal: true,
            imageMode: 'edit',
            imageFile: undefined,
            imageUploads: [],
            imageForm: {
                id: image.id,
                title: image.title,
                titleHe: image.titleHe || '',
                notes: image.notes,
                notesHe: image.notesHe || '',
                imageBase64: image.imageBase64,
                imageMime: image.imageMime,
                imageName: image.imageName,
            },
        })
    }

    closeDrinkModal = () => {
        this.setState({ showDrinkModal: false })
    }

    closeImageModal = () => {
        this.setState({ showImageModal: false })
    }

    handleSyrupFormChange = (field: keyof SyrupForm, value: any) => {
        this.setState(prev => ({
            syrupForm: { ...prev.syrupForm, [field]: value },
        }))
    }

    handleDrinkFormChange = (field: keyof DrinkForm, value: any) => {
        this.setState(prev => ({
            drinkForm: { ...prev.drinkForm, [field]: value },
        }))
    }

    handleDrinkTypeChange = (drinkType: DrinkForm['drinkType']) => {
        this.setState(prev => ({
            drinkForm: {
                ...prev.drinkForm,
                drinkType,
                ...(drinkType === 'base'
                    ? {
                          flavourChannel: LTSP101_NO_FLAVOUR_CHANNEL,
                          brixRatioA: LTSP101_BASE_BRIX_RATIO_A,
                          brixRatioB: LTSP101_BASE_BRIX_RATIO_B,
                      }
                    : {}),
            },
        }))
    }

    handleImageFormChange = (field: keyof ImageForm, value: any) => {
        this.setState(prev => ({
            imageForm: { ...prev.imageForm, [field]: value },
        }))
    }

    handleImageChange = async (files: MyFile[]) => {
        if (!files.length) return
        const uploads: ImageUploadItem[] = []
        for (const file of files) {
            const base64 = await getBase64(file)
            const fileTitle = file.name.replace(/\.[^/.]+$/, '')
            uploads.push({
                file,
                title: fileTitle,
                titleHe: '',
                notes: '',
                notesHe: '',
                imageBase64: base64,
                imageMime: file.type,
                imageName: file.name,
            })
        }
        this.setState(prev => ({
            imageFile: files[0],
            imageUploads: prev.imageMode === 'create' ? uploads : prev.imageUploads,
            imageForm:
                prev.imageMode === 'create'
                    ? prev.imageForm
                    : {
                          ...prev.imageForm,
                          imageBase64: uploads[0]?.imageBase64 ?? prev.imageForm.imageBase64,
                          imageMime: uploads[0]?.imageMime ?? prev.imageForm.imageMime,
                          imageName: uploads[0]?.imageName ?? prev.imageForm.imageName,
                      },
        }))
    }

    saveSyrup = async () => {
        const { syrupForm, syrupMode } = this.state
        if (!syrupForm.name.trim() || !syrupForm.nameHe.trim()) {
            toast.error(getString('ltsp101_catalog.required_fields'))
            return
        }
        try {
            if (syrupMode === 'create') {
                await createLtsp101CatalogSyrup({
                    name: syrupForm.name,
                    description: syrupForm.description,
                    nameHe: syrupForm.nameHe,
                    descriptionHe: syrupForm.descriptionHe,
                    packageSize: syrupForm.packageSize,
                    imageId: syrupForm.imageId ?? null,
                })
                toast.success(getString('ltsp101_catalog.syrup_create_success'))
            } else if (syrupForm.id !== undefined) {
                await updateLtsp101CatalogSyrup(syrupForm.id, {
                    name: syrupForm.name,
                    description: syrupForm.description,
                    nameHe: syrupForm.nameHe,
                    descriptionHe: syrupForm.descriptionHe,
                    packageSize: syrupForm.packageSize,
                    imageId: syrupForm.imageId ?? null,
                })
                toast.success(getString('ltsp101_catalog.syrup_update_success'))
            }
            this.closeSyrupModal()
            await this.loadAll()
        } catch (error: any) {
            toast.error(error?.message || getString('ltsp101_catalog.save_error'))
        }
    }

    saveDrink = async () => {
        const { drinkForm, drinkMode } = this.state
        const isBaseDrink = drinkForm.drinkType === 'base'
        const flavourChannel = isBaseDrink ? LTSP101_NO_FLAVOUR_CHANNEL : drinkForm.flavourChannel
        const brixRatioA = isBaseDrink ? LTSP101_BASE_BRIX_RATIO_A : drinkForm.brixRatioA
        const brixRatioB = isBaseDrink ? LTSP101_BASE_BRIX_RATIO_B : drinkForm.brixRatioB
        if (!drinkForm.title.trim() || !drinkForm.titleHe.trim()) {
            toast.error(getString('ltsp101_catalog.required_fields'))
            return
        }
        try {
            if (drinkMode === 'create') {
                await createLtsp101CatalogDrink({
                    title: drinkForm.title,
                    description: drinkForm.description,
                    titleHe: drinkForm.titleHe,
                    descriptionHe: drinkForm.descriptionHe,
                    baseChannel: drinkForm.baseChannel,
                    flavourChannel,
                    brixRatioA,
                    brixRatioB,
                })
                toast.success(getString('ltsp101_catalog.drink_create_success'))
            } else if (drinkForm.id !== undefined) {
                await updateLtsp101CatalogDrink(drinkForm.id, {
                    title: drinkForm.title,
                    description: drinkForm.description,
                    titleHe: drinkForm.titleHe,
                    descriptionHe: drinkForm.descriptionHe,
                    baseChannel: drinkForm.baseChannel,
                    flavourChannel,
                    brixRatioA,
                    brixRatioB,
                })
                toast.success(getString('ltsp101_catalog.drink_update_success'))
            }
            this.closeDrinkModal()
            await this.loadAll()
        } catch (error: any) {
            toast.error(error?.message || getString('ltsp101_catalog.save_error'))
        }
    }

    saveImage = async () => {
        const { imageForm, imageMode, imageUploads } = this.state
        if (imageMode === 'create') {
            const invalid = imageUploads.find(item => !item.title.trim() || !item.titleHe.trim() || !item.imageBase64)
            if (invalid || imageUploads.length === 0) {
                toast.error(getString('ltsp101_catalog.required_fields'))
                return
            }
        } else if (!imageForm.title.trim() || !imageForm.titleHe.trim() || !imageForm.imageBase64) {
            toast.error(getString('ltsp101_catalog.required_fields'))
            return
        }
        try {
            if (imageMode === 'create') {
                for (const item of imageUploads) {
                    await createLtsp101CatalogImage({
                        title: item.title,
                        titleHe: item.titleHe,
                        notes: item.notes,
                        notesHe: item.notesHe,
                        imageBase64: item.imageBase64,
                        imageMime: item.imageMime,
                        imageName: item.imageName,
                    })
                }
                toast.success(getString('ltsp101_catalog.image_create_success'))
            } else if (imageForm.id !== undefined) {
                await updateLtsp101CatalogImage(imageForm.id, {
                    title: imageForm.title,
                    titleHe: imageForm.titleHe,
                    notes: imageForm.notes,
                    notesHe: imageForm.notesHe,
                    imageBase64: imageForm.imageBase64,
                    imageMime: imageForm.imageMime,
                    imageName: imageForm.imageName,
                })
                toast.success(getString('ltsp101_catalog.image_update_success'))
            }
            this.closeImageModal()
            await this.loadAll()
        } catch (error: any) {
            toast.error(error?.message || getString('ltsp101_catalog.save_error'))
        }
    }

    deleteSyrup = async (id: number) => {
        if (!window.confirm(getString('ltsp101_catalog.syrup_delete_confirm'))) return
        try {
            await deleteLtsp101CatalogSyrup(id)
            toast.success(getString('ltsp101_catalog.syrup_delete_success'))
            await this.loadAll()
        } catch (error: any) {
            toast.error(error?.message || getString('ltsp101_catalog.delete_error'))
        }
    }

    deleteDrink = async (id: number) => {
        if (!window.confirm(getString('ltsp101_catalog.drink_delete_confirm'))) return
        try {
            await deleteLtsp101CatalogDrink(id)
            toast.success(getString('ltsp101_catalog.drink_delete_success'))
            await this.loadAll()
        } catch (error: any) {
            toast.error(error?.message || getString('ltsp101_catalog.delete_error'))
        }
    }

    deleteImage = async (id: number) => {
        if (!window.confirm(getString('ltsp101_catalog.image_delete_confirm'))) return
        try {
            await deleteLtsp101CatalogImage(id)
            toast.success(getString('ltsp101_catalog.image_delete_success'))
            await this.loadAll()
        } catch (error: any) {
            toast.error(error?.message || getString('ltsp101_catalog.delete_error'))
        }
    }

    renderTabs() {
        const { tab } = this.state
        return (
            <div className="flex flex-wrap gap-2 mb-6">
                <button
                    className={
                        tab === 'syrups'
                            ? 'px-4 py-2 rounded-lg bg-indigo-600 text-white'
                            : 'px-4 py-2 rounded-lg bg-white text-gray-800 dark:bg-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                    onClick={() => this.setState({ tab: 'syrups' })}
                >
                    {getString('ltsp101_catalog.syrups_tab')}
                </button>
                <button
                    className={
                        tab === 'drinks'
                            ? 'px-4 py-2 rounded-lg bg-indigo-600 text-white'
                            : 'px-4 py-2 rounded-lg bg-white text-gray-800 dark:bg-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                    onClick={() => this.setState({ tab: 'drinks' })}
                >
                    {getString('ltsp101_catalog.drinks_tab')}
                </button>
                <button
                    className={
                        tab === 'images'
                            ? 'px-4 py-2 rounded-lg bg-indigo-600 text-white'
                            : 'px-4 py-2 rounded-lg bg-white text-gray-800 dark:bg-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }
                    onClick={() => this.setState({ tab: 'images' })}
                >
                    {getString('ltsp101_catalog.images_tab')}
                </button>
            </div>
        )
    }

    renderDocs() {
        const { showDocs } = this.state
        if (!showDocs) {
            return (
                <div className="mb-6">
                    <button
                        onClick={() => this.setState({ showDocs: true })}
                        className="px-4 py-2 border dark:border-gray-700 rounded bg-white dark:bg-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                        {getString('ltsp101_catalog.api_docs_show')}
                    </button>
                </div>
            )
        }

        return (
            <div className="mb-6 border dark:border-gray-700 rounded-lg p-4 bg-white dark:bg-gray-800">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold dark:text-white">{getString('ltsp101_catalog.api_docs_title')}</h2>
                    <button
                        onClick={() => this.setState({ showDocs: false })}
                        className="px-3 py-1 text-sm border dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                    >
                        {getString('ltsp101_catalog.api_docs_hide')}
                    </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    {getString('ltsp101_catalog.api_docs_intro')}
                </p>
                <div className="text-sm text-gray-700 dark:text-gray-200 mb-3">
                    <div className="font-semibold mb-1">{getString('ltsp101_catalog.api_docs_auth_title')}</div>
                    <div>{getString('ltsp101_catalog.api_docs_auth_body')}</div>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-200 mb-3">
                    <div className="font-semibold mb-1">{getString('ltsp101_catalog.api_docs_endpoints_title')}</div>
                    <div className="space-y-1">
                        <div>
                            {getString('ltsp101_catalog.api_docs_endpoint_all')} <code>{`${HOST_URL}ltsp101/catalog/public`}</code>
                        </div>
                        <div>
                            {getString('ltsp101_catalog.api_docs_endpoint_syrups')}{' '}
                            <code>{`${HOST_URL}ltsp101/catalog/syrups/public`}</code>
                        </div>
                        <div>
                            {getString('ltsp101_catalog.api_docs_endpoint_drinks')}{' '}
                            <code>{`${HOST_URL}ltsp101/catalog/drinks/public`}</code>
                        </div>
                        <div>
                            {getString('ltsp101_catalog.api_docs_endpoint_images')}{' '}
                            <code>{`${HOST_URL}ltsp101/catalog/images/public`}</code>
                        </div>
                        <div>
                            {getString('ltsp101_catalog.api_docs_endpoint_names')}{' '}
                            <code>{`${HOST_URL}ltsp101/catalog/names/public`}</code>
                        </div>
                        <div>
                            {getString('ltsp101_catalog.api_docs_endpoint_image_data')}{' '}
                            <code>{`${HOST_URL}ltsp101/catalog/images/public/:id/data`}</code>
                        </div>
                    </div>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-200 mb-3">
                    <div className="font-semibold mb-1">{getString('ltsp101_catalog.api_docs_example_title')}</div>
                    <pre className="text-xs bg-gray-100 dark:bg-gray-900 dark:text-gray-100 p-3 rounded overflow-x-auto">
                        {`GET ${HOST_URL}ltsp101/catalog/public
X-Api-Key: YOUR_CATALOG_API_KEY`}
                    </pre>
                </div>
                <div className="text-sm text-gray-700 dark:text-gray-200">
                    <div className="font-semibold mb-1">{getString('ltsp101_catalog.api_docs_response_title')}</div>
                    <pre className="text-xs bg-gray-100 dark:bg-gray-900 dark:text-gray-100 p-3 rounded overflow-x-auto">
                        {`{
  "data": {
    "syrups": [],
    "drinks": [],
    "images": []
  },
  "message": "listCatalogPublic"
}`}
                    </pre>
                </div>
            </div>
        )
    }

    renderSearch() {
        return (
            <div className="mb-6">
                <input
                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-800 dark:text-white"
                    placeholder={getString('ltsp101_catalog.search')}
                    value={this.state.search}
                    onChange={e => this.handleSearchChange(e.target.value)}
                />
            </div>
        )
    }

    renderSyrups() {
        const { syrups, search, images } = this.state
        const isHebrew = getLocale() === 'he'
        const imageMap = new Map(images.map(image => [image.id, image]))
        const filtered = syrups.filter(syrup => {
            const name = (isHebrew && syrup.nameHe ? syrup.nameHe : syrup.name).toLowerCase()
            return name.includes(search.toLowerCase())
        })
        return (
            <div>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold dark:text-white">{getString('ltsp101_catalog.syrups_tab')}</h2>
                    <button
                        onClick={this.openCreateSyrup}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        {getString('ltsp101_catalog.add_syrup')}
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(syrup => {
                        const displayName = isHebrew && syrup.nameHe ? syrup.nameHe : syrup.name
                        const displayDescription = isHebrew && syrup.descriptionHe ? syrup.descriptionHe : syrup.description
                        const image = syrup.imageId ? imageMap.get(syrup.imageId) : undefined
                        const imageUrl = image ? `data:${image.imageMime};base64,${image.imageBase64}` : undefined
                        return (
                            <div key={syrup.id} className="border dark:border-gray-700 rounded-lg p-4 shadow hover:shadow-md dark:bg-gray-800">
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-12 h-12 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden flex items-center justify-center">
                                        {imageUrl ? (
                                            <img src={imageUrl} alt={displayName} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">{getString('ltsp101_catalog.no_image')}</span>
                                        )}
                                    </div>
                                    <h3 className="text-lg font-semibold dark:text-white">{displayName}</h3>
                                </div>
                                <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">{displayDescription}</p>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    {getString('ltsp101_catalog.package_size')}: {syrup.packageSize}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => this.openEditSyrup(syrup)}
                                        className="flex-1 px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600"
                                    >
                                        {getString('common.edit')}
                                    </button>
                                    <button
                                        onClick={() => this.deleteSyrup(syrup.id)}
                                        className="flex-1 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                                    >
                                        {getString('common.delete')}
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                    {filtered.length === 0 && (
                        <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                            {getString('ltsp101_catalog.syrups_empty')}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    renderDrinks() {
        const { drinks, search } = this.state
        const isHebrew = getLocale() === 'he'
        const filtered = drinks.filter(drink => {
            const title = (isHebrew && drink.titleHe ? drink.titleHe : drink.title).toLowerCase()
            return title.includes(search.toLowerCase())
        })
        return (
            <div>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold dark:text-white">{getString('ltsp101_catalog.drinks_tab')}</h2>
                    <button
                        onClick={this.openCreateDrink}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        {getString('ltsp101_catalog.add_drink')}
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(drink => {
                        const displayTitle = isHebrew && drink.titleHe ? drink.titleHe : drink.title
                        const displayDescription = isHebrew && drink.descriptionHe ? drink.descriptionHe : drink.description
                        return (
                            <div key={drink.id} className="border dark:border-gray-700 rounded-lg p-4 shadow hover:shadow-md dark:bg-gray-800">
                                <h3 className="text-lg font-semibold mb-2 dark:text-white">{displayTitle}</h3>
                                <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">{displayDescription}</p>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    {getString('ltsp101_catalog.base_channel')}: {drink.baseChannel + 1} | {getString('ltsp101_catalog.flavour_channel')}: {drink.flavourChannel === 255 ? getString('ltsp101_catalog.no_flavour') : drink.flavourChannel + 1}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                    {getString('ltsp101_catalog.brix_ratio')}: {drink.brixRatioA}/{drink.brixRatioB}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => this.openEditDrink(drink)}
                                        className="flex-1 px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600"
                                    >
                                        {getString('common.edit')}
                                    </button>
                                    <button
                                        onClick={() => this.deleteDrink(drink.id)}
                                        className="flex-1 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                                    >
                                        {getString('common.delete')}
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                    {filtered.length === 0 && (
                        <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                            {getString('ltsp101_catalog.drinks_empty')}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    renderSyrupModal() {
        const { showSyrupModal, syrupMode, syrupForm, images } = this.state
        if (!showSyrupModal) return undefined
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-auto">
                    <h3 className="text-xl font-bold mb-4 dark:text-white">
                        {syrupMode === 'create' ? getString('ltsp101_catalog.add_syrup') : getString('ltsp101_catalog.edit_syrup')}
                    </h3>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.name_en')} *
                                </label>
                                <input
                                    type="text"
                                    value={syrupForm.name}
                                    onChange={e => this.handleSyrupFormChange('name', e.target.value)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    maxLength={100}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.name_he')} *
                                </label>
                                <input
                                    type="text"
                                    value={syrupForm.nameHe}
                                    onChange={e => this.handleSyrupFormChange('nameHe', e.target.value)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    dir="rtl"
                                    maxLength={100}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.description_en')}
                                </label>
                                <textarea
                                    value={syrupForm.description}
                                    onChange={e => this.handleSyrupFormChange('description', e.target.value)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    rows={3}
                                    maxLength={500}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.description_he')}
                                </label>
                                <textarea
                                    value={syrupForm.descriptionHe}
                                    onChange={e => this.handleSyrupFormChange('descriptionHe', e.target.value)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    rows={3}
                                    dir="rtl"
                                    maxLength={500}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1 dark:text-white">
                                {getString('ltsp101_catalog.package_size')}
                            </label>
                            <input
                                type="number"
                                value={syrupForm.packageSize}
                                onChange={e => this.handleSyrupFormChange('packageSize', parseFloat(e.target.value) || 0)}
                                className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                min={0}
                                step={0.1}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1 dark:text-white">
                                {getString('ltsp101_catalog.image')}
                            </label>
                            <select
                                value={syrupForm.imageId ?? 0}
                                onChange={e => this.handleSyrupFormChange('imageId', parseInt(e.target.value) || null)}
                                className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                            >
                                <option value={0}>{getString('ltsp101_catalog.no_image')}</option>
                                {images.map(image => (
                                    <option key={image.id} value={image.id}>
                                        {getLocale() === 'he' && image.titleHe ? image.titleHe : image.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            onClick={this.closeSyrupModal}
                            className="px-4 py-2 border dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                        >
                            {getString('common.cancel')}
                        </button>
                        <button
                            onClick={this.saveSyrup}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            {getString('common.save')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    renderImages() {
        const { images, search } = this.state
        const isHebrew = getLocale() === 'he'
        const filtered = images.filter(image => {
            const title = (isHebrew && image.titleHe ? image.titleHe : image.title).toLowerCase()
            return title.includes(search.toLowerCase())
        })
        return (
            <div>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold dark:text-white">{getString('ltsp101_catalog.images_tab')}</h2>
                    <button
                        onClick={this.openCreateImage}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                        {getString('ltsp101_catalog.add_image')}
                    </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(image => {
                        const imageUrl = `data:${image.imageMime};base64,${image.imageBase64}`
                        const displayTitle = isHebrew && image.titleHe ? image.titleHe : image.title
                        const displayNotes = isHebrew && image.notesHe ? image.notesHe : image.notes
                        return (
                            <div key={image.id} className="border dark:border-gray-700 rounded-lg p-4 shadow hover:shadow-md dark:bg-gray-800">
                                <div className="w-full h-40 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden mb-3">
                                    <img src={imageUrl} alt={displayTitle} className="w-full h-full object-cover" />
                                </div>
                                <h3 className="text-lg font-semibold mb-2 dark:text-white">{displayTitle}</h3>
                                <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">{displayNotes}</p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => this.openEditImage(image)}
                                        className="flex-1 px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600"
                                    >
                                        {getString('common.edit')}
                                    </button>
                                    <button
                                        onClick={() => this.deleteImage(image.id)}
                                        className="flex-1 px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                                    >
                                        {getString('common.delete')}
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                    {filtered.length === 0 && (
                        <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">
                            {getString('ltsp101_catalog.images_empty')}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    renderDrinkModal() {
        const { showDrinkModal, drinkMode, drinkForm } = this.state
        if (!showDrinkModal) return undefined
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-auto">
                    <h3 className="text-xl font-bold mb-4 dark:text-white">
                        {drinkMode === 'create' ? getString('ltsp101_catalog.add_drink') : getString('ltsp101_catalog.edit_drink')}
                    </h3>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.title_en')} *
                                </label>
                                <input
                                    type="text"
                                    value={drinkForm.title}
                                    onChange={e => this.handleDrinkFormChange('title', e.target.value)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    maxLength={100}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.title_he')} *
                                </label>
                                <input
                                    type="text"
                                    value={drinkForm.titleHe}
                                    onChange={e => this.handleDrinkFormChange('titleHe', e.target.value)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    dir="rtl"
                                    maxLength={100}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.description_en')}
                                </label>
                                <textarea
                                    value={drinkForm.description}
                                    onChange={e => this.handleDrinkFormChange('description', e.target.value)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    rows={3}
                                    maxLength={500}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.description_he')}
                                </label>
                                <textarea
                                    value={drinkForm.descriptionHe}
                                    onChange={e => this.handleDrinkFormChange('descriptionHe', e.target.value)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    rows={3}
                                    dir="rtl"
                                    maxLength={500}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getLocale() === 'he' ? 'סוג משקה' : 'Drink type'}
                                </label>
                                <select
                                    value={drinkForm.drinkType}
                                    onChange={e => this.handleDrinkTypeChange(e.target.value as DrinkForm['drinkType'])}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                >
                                    <option value="juice">{getLocale() === 'he' ? 'מיץ' : 'Juice'}</option>
                                    <option value="soda">{getLocale() === 'he' ? 'גזוז' : 'Soda'}</option>
                                    <option value="base">{getLocale() === 'he' ? 'בסיס' : 'Base'}</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.base_channel')}
                                </label>
                                <input
                                    type="number"
                                    value={drinkForm.baseChannel}
                                    onChange={e => this.handleDrinkFormChange('baseChannel', parseInt(e.target.value) || 0)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    min={0}
                                    max={255}
                                />
                            </div>
                            {drinkForm.drinkType !== 'base' && (
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.flavour_channel')}
                                </label>
                                <input
                                    type="number"
                                    value={drinkForm.flavourChannel}
                                    onChange={e => this.handleDrinkFormChange('flavourChannel', parseInt(e.target.value) || 255)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    min={0}
                                    max={255}
                                />
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {getString('ltsp101_catalog.no_flavour_hint')}
                                </div>
                            </div>
                            )}
                        </div>
                        {drinkForm.drinkType !== 'base' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.brix_ratio_a')}
                                </label>
                                <input
                                    type="number"
                                    value={drinkForm.brixRatioA}
                                    onChange={e => this.handleDrinkFormChange('brixRatioA', parseInt(e.target.value) || 0)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    min={0}
                                    max={255}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 dark:text-white">
                                    {getString('ltsp101_catalog.brix_ratio_b')}
                                </label>
                                <input
                                    type="number"
                                    value={drinkForm.brixRatioB}
                                    onChange={e => this.handleDrinkFormChange('brixRatioB', parseInt(e.target.value) || 0)}
                                    className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                    min={0}
                                    max={255}
                                />
                            </div>
                        </div>
                        )}
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            onClick={this.closeDrinkModal}
                            className="px-4 py-2 border dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                        >
                            {getString('common.cancel')}
                        </button>
                        <button
                            onClick={this.saveDrink}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            {getString('common.save')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    renderImageModal() {
        const { showImageModal, imageMode, imageForm, imageFile, imageUploads } = this.state
        if (!showImageModal) return undefined
        const imageUrl = imageForm.imageBase64
            ? `data:${imageForm.imageMime};base64,${imageForm.imageBase64}`
            : imageFile?.preview?.url
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto z-50 p-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-auto">
                    <h3 className="text-xl font-bold mb-4 dark:text-white">
                        {imageMode === 'create' ? getString('ltsp101_catalog.add_image') : getString('ltsp101_catalog.edit_image')}
                    </h3>
                    <div className="space-y-4">
                        {imageMode === 'create' ? (
                            <div className="space-y-2">
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {getString('ltsp101_catalog.image_title_hint')}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {getString('ltsp101_catalog.image_notes_hint')}
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1 dark:text-white">
                                            {getString('ltsp101_catalog.image_title_en')} *
                                        </label>
                                        <input
                                            type="text"
                                            value={imageForm.title}
                                            onChange={e => this.handleImageFormChange('title', e.target.value)}
                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                            maxLength={100}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1 dark:text-white">
                                            {getString('ltsp101_catalog.image_title_he')} *
                                        </label>
                                        <input
                                            type="text"
                                            value={imageForm.titleHe}
                                            onChange={e => this.handleImageFormChange('titleHe', e.target.value)}
                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                            dir="rtl"
                                            maxLength={100}
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1 dark:text-white">
                                            {getString('ltsp101_catalog.image_notes_en')}
                                        </label>
                                        <textarea
                                            value={imageForm.notes}
                                            onChange={e => this.handleImageFormChange('notes', e.target.value)}
                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                            rows={3}
                                            maxLength={500}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1 dark:text-white">
                                            {getString('ltsp101_catalog.image_notes_he')}
                                        </label>
                                        <textarea
                                            value={imageForm.notesHe}
                                            onChange={e => this.handleImageFormChange('notesHe', e.target.value)}
                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                            rows={3}
                                            dir="rtl"
                                            maxLength={500}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        <div>
                            <label className="block text-sm font-medium mb-1 dark:text-white">
                                {getString('ltsp101_catalog.image')}
                            </label>
                            <Files
                                multiple={imageMode === 'create'}
                                maxFiles={imageMode === 'create' ? 20 : 1}
                                accepts={['image/*']}
                                maxFileSize={2_000_000}
                                onChange={this.handleImageChange}
                                onError={() => {}}
                                className="border border-dashed border-gray-300 dark:border-gray-600 rounded p-4"
                            >
                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                    {imageMode === 'create'
                                        ? getString('ltsp101_catalog.image_drop_multiple')
                                        : getString('ltsp101_catalog.image_drop')}
                                </div>
                            </Files>
                            {imageMode === 'edit' && imageUrl && (
                                <div className="mt-3">
                                    <img src={imageUrl} alt="preview" className="w-32 h-32 object-cover rounded" />
                                </div>
                            )}
                            {imageMode === 'create' && imageUploads.length > 0 && (
                                <div className="mt-4 space-y-3">
                                    {imageUploads.map((item, index) => (
                                        <div
                                            key={`${item.imageName}-${index}`}
                                            className="border dark:border-gray-700 rounded p-3"
                                        >
                                            <div className="flex items-start gap-3">
                                                {item.file.preview?.url && (
                                                    <img
                                                        src={item.file.preview.url}
                                                        alt={item.title}
                                                        className="w-16 h-16 object-cover rounded"
                                                    />
                                                )}
                                                <div className="flex-1 space-y-2">
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1 dark:text-white">
                                                            {getString('ltsp101_catalog.image_title_en')}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={item.title}
                                                            onChange={e =>
                                                                this.setState(prev => {
                                                                    const next = [...prev.imageUploads]
                                                                    next[index] = { ...next[index], title: e.target.value }
                                                                    return { imageUploads: next }
                                                                })
                                                            }
                                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                            maxLength={100}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1 dark:text-white">
                                                            {getString('ltsp101_catalog.image_title_he')}
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={item.titleHe}
                                                            onChange={e =>
                                                                this.setState(prev => {
                                                                    const next = [...prev.imageUploads]
                                                                    next[index] = { ...next[index], titleHe: e.target.value }
                                                                    return { imageUploads: next }
                                                                })
                                                            }
                                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                            dir="rtl"
                                                            maxLength={100}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1 dark:text-white">
                                                            {getString('ltsp101_catalog.image_notes_en')}
                                                        </label>
                                                        <textarea
                                                            value={item.notes}
                                                            onChange={e =>
                                                                this.setState(prev => {
                                                                    const next = [...prev.imageUploads]
                                                                    next[index] = { ...next[index], notes: e.target.value }
                                                                    return { imageUploads: next }
                                                                })
                                                            }
                                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                            rows={2}
                                                            maxLength={500}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium mb-1 dark:text-white">
                                                            {getString('ltsp101_catalog.image_notes_he')}
                                                        </label>
                                                        <textarea
                                                            value={item.notesHe}
                                                            onChange={e =>
                                                                this.setState(prev => {
                                                                    const next = [...prev.imageUploads]
                                                                    next[index] = { ...next[index], notesHe: e.target.value }
                                                                    return { imageUploads: next }
                                                                })
                                                            }
                                                            className="w-full border dark:border-gray-700 rounded px-3 py-2 dark:bg-gray-700 dark:text-white"
                                                            rows={2}
                                                            dir="rtl"
                                                            maxLength={500}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-6">
                        <button
                            onClick={this.closeImageModal}
                            className="px-4 py-2 border dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-white"
                        >
                            {getString('common.cancel')}
                        </button>
                        <button
                            onClick={this.saveImage}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                            {getString('common.save')}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    render() {
        const { loading, tab } = this.state
        return (
            <div className="container flex flex-col">
                {this.renderTabs()}
                {this.renderDocs()}
                {this.renderSearch()}
                {loading ? (
                    <div className="text-center py-8">{getString('common.loading')}</div>
                ) : tab === 'syrups' ? (
                    this.renderSyrups()
                ) : tab === 'drinks' ? (
                    this.renderDrinks()
                ) : (
                    this.renderImages()
                )}
                {this.renderSyrupModal()}
                {this.renderDrinkModal()}
                {this.renderImageModal()}
            </div>
        )
    }
}

export default Ltsp101Catalog
